import * as React from 'react'
import { CaretDown } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { type BadgeColor } from './badge'
import { ActivityIcon, type ActivityType } from './activity-icons'

/**
 * ActivityTimeline — a date-grouped feed of events (the CRM "event / activity
 * history"). Each event is one card in a day section; consecutive cards in a day
 * are joined by a vertical connector through their avatars, so a day of activity
 * reads as a single thread. Powers the company/deal activity history and the
 * Action Center panel's "Company Activity" tab.
 *
 * The component owns the *structure* — day grouping, ordering (newest first),
 * connectors, and the per-day "+N more" collapse — and stays unopinionated about
 * the *sentence*. Each item supplies:
 *
 *   icon     ReactNode    the event glyph (Phosphor), shown in the avatar chip
 *   color    BadgeColor   avatar tint (ring + icon), default 'success'
 *   actor    ReactNode    who did it — rendered bold (person or automation name)
 *   summary  ReactNode    the action sentence; embed <strong> for entities and
 *                         inline <Badge>s for stages — <strong> is auto-styled to
 *                         the bold title tone so callers don't repeat classes
 *   detail   ReactNode    optional secondary block below (note text, tag chips,
 *                         a "Request cancelled" line, …)
 *   timestamp            Date | string | number — used for grouping + the time
 *
 * Ordering is derived: items are sorted newest-first and bucketed by local day,
 * so callers can pass events in any order. Day headers read "Today" / "Yesterday"
 * for the two most recent days (relative to `now`), else "Tuesday, Jun 30".
 */

export interface ActivityItem {
  /** Stable key for the row. */
  id: string | number
  /** When the event happened — drives day grouping and the displayed time. */
  timestamp: Date | string | number
  /**
   * Activity category — resolves the avatar icon + tint from the shared
   * {@link activityTypeConfig} library. Override either with `icon` / `color`.
   */
  type?: ActivityType
  /** Event glyph shown inside the avatar chip (overrides `type`'s icon). */
  icon?: React.ReactNode
  /** Avatar tint from the badge palette (overrides `type`'s color; default 'success'). */
  color?: BadgeColor
  /** Who performed the action — rendered bold (person or automation name). */
  actor?: React.ReactNode
  /**
   * The action sentence, rendered in the muted body tone. Wrap entities in
   * `<strong>` (auto-promoted to the bold title tone) and drop inline `<Badge>`s
   * for stages/labels.
   */
  summary: React.ReactNode
  /**
   * Secondary content below the sentence (chips, a "Request cancelled" line, …).
   * On an **expandable** row (see `expandedContent`) this doubles as the collapsed
   * one-line **preview** — it's truncated with a "…" so the row reads as having
   * more to open. Give expandable rows a `detail` so they show that hint.
   */
  detail?: React.ReactNode
  /**
   * Richer content revealed when the row is clicked (e.g. a full email body or
   * note). Providing it makes the row an expand/collapse disclosure with a
   * chevron; omit for a static row. Used for email/note events.
   */
  expandedContent?: React.ReactNode
}

export interface ActivityTimelineProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Events to render. Order-independent — sorted newest-first internally. */
  items: ActivityItem[]
  /**
   * Collapse each day to this many rows, with a "+N more" expander for the rest.
   * Omit to always show every event.
   */
  collapseAfter?: number
  /** "Now" for the relative Today/Yesterday day labels. Default: current time. */
  now?: Date
  /** Shown in place of the feed when `items` is empty. */
  emptyState?: React.ReactNode
  /**
   * Tint each avatar by its activity category instead of the uniform brand tone.
   * Default false — the glyph carries the category, so color stays reserved for
   * meaning. A per-item `color` still overrides regardless.
   */
  tintedIcons?: boolean
  /** Density — `md` (default) or `sm` for compact feeds (side panels, cards). */
  size?: ActivityTimelineSize
}

const toDate = (t: Date | string | number): Date =>
  t instanceof Date ? t : new Date(t)

const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`

function formatDayLabel(d: Date, now: Date): string {
  const key = dayKey(d)
  if (key === dayKey(now)) return 'Today'
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (key === dayKey(yesterday)) return 'Yesterday'
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  })
}

const formatTime = (d: Date): string =>
  d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

interface DayGroup {
  key: string
  label: string
  items: { item: ActivityItem; date: Date }[]
}

/** Bucket items into newest-first day groups (and sort within each day). */
function groupByDay(items: ActivityItem[], now: Date): DayGroup[] {
  const dated = items.map((item) => ({ item, date: toDate(item.timestamp) }))
  const buckets = new Map<string, DayGroup>()
  for (const entry of dated) {
    const key = dayKey(entry.date)
    let group = buckets.get(key)
    if (!group) {
      group = { key, label: formatDayLabel(entry.date, now), items: [] }
      buckets.set(key, group)
    }
    group.items.push(entry)
  }
  const groups = [...buckets.values()]
  // Sort within each day first (newest → oldest), then order the day sections by
  // each group's newest item so section order is unambiguous for any input order.
  for (const group of groups) {
    group.items.sort((a, b) => b.date.getTime() - a.date.getTime())
  }
  groups.sort((a, b) => b.items[0].date.getTime() - a.items[0].date.getTime())
  return groups
}

export type ActivityTimelineSize = 'sm' | 'md'

/**
 * Per-size metrics. `railTop` is the height of the connector stub above the
 * avatar — tuned so the avatar centers on the card's first (header) line rather
 * than the whole card, keeping it put when the card expands. `rail` width must
 * track the `ActivityIcon` chip size for this `size` (sm: w-7 = size-7 = 28px,
 * md: w-9 = size-9 = 36px) so the avatar fits the rail and the connector centers.
 */
const timelineSize = {
  sm: {
    groupGap: 'gap-4',
    dayLabel: 'text-xs',
    rail: 'w-7',
    railTop: 'h-1',
    card: 'my-0.5 ml-2 px-3 py-2',
    summary: 'text-xs',
    detail: 'mt-1 text-xs',
    previewText: 'text-xs',
  },
  md: {
    groupGap: 'gap-6',
    dayLabel: 'text-sm',
    rail: 'w-9',
    railTop: 'h-2',
    card: 'my-1 ml-3 px-4 py-3',
    summary: 'text-sm',
    detail: 'mt-1.5 text-sm',
    previewText: 'text-sm',
  },
} as const

/** One event: connector rail (avatar + line) beside a white content card. */
const ActivityRow = ({
  item,
  date,
  isFirst,
  isLast,
  tinted,
  size,
}: {
  item: ActivityItem
  date: Date
  isFirst: boolean
  isLast: boolean
  tinted?: boolean
  size: ActivityTimelineSize
}) => {
  const s = timelineSize[size]
  const [open, setOpen] = React.useState(false)
  const expandable = item.expandedContent != null
  const panelId = React.useId()

  const summaryClass = cn(
    'min-w-0 text-fbui-text-secondary [&_strong]:font-medium [&_strong]:text-fbui-text-title',
    s.summary
  )
  const sentence = (
    <>
      {item.actor != null && (
        <span className="font-semibold text-fbui-text-title">{item.actor}</span>
      )}
      {item.actor != null && ' '}
      {item.summary}
    </>
  )
  const time = (
    <time className="shrink-0 whitespace-nowrap text-xs tabular-nums text-fbui-text-secondary">
      {formatTime(date)}
    </time>
  )

  return (
    <li className="flex items-stretch">
      {/* Rail: the connector fills above + below the avatar, and a fixed top stub
          (`railTop`) drops the avatar so it centers on the card's first line —
          so it stays put when the card expands, not centered on the tall card. */}
      <div
        className={cn(
          'relative flex shrink-0 flex-col items-center self-stretch',
          s.rail
        )}
      >
        <span
          aria-hidden="true"
          className={cn('w-px shrink-0', s.railTop, !isFirst && 'bg-fbui-gray-200')}
        />
        {/* Avatar resolves its icon from `type`; monochrome unless `tinted`. */}
        <ActivityIcon
          type={item.type}
          icon={item.icon}
          color={item.color}
          tinted={tinted}
          size={size}
          className="relative z-10"
        />
        <span
          aria-hidden="true"
          className={cn('w-px flex-1', !isLast && 'bg-fbui-gray-200')}
        />
      </div>
      <div
        className={cn(
          'min-w-0 flex-1 rounded-lg bg-fbui-base-white shadow-fbui-xs',
          s.card
        )}
      >
        {expandable ? (
          // Click-to-expand disclosure (email/note): the header + a one-line
          // truncated preview are the toggle; the "…" cutoff signals there's more
          // below, and the chevron flips on open.
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            // Only reference the panel while it's rendered (it's absent when collapsed).
            aria-controls={open ? panelId : undefined}
            className="flex w-full flex-col gap-1 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
          >
            <span className="flex w-full items-start justify-between gap-2">
              <span className={summaryClass}>{sentence}</span>
              {/* Header holds only the time (so every card's time stays
                  right-aligned); the caret rides the preview line below. When
                  expanded there's no preview line, so the caret returns here. */}
              <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
                {time}
                {(open || item.detail == null) && (
                  <CaretDown
                    aria-hidden="true"
                    className={cn(
                      'size-3.5 text-fbui-gray-400',
                      open && 'rotate-180'
                    )}
                  />
                )}
              </span>
            </span>
            {!open && item.detail != null && (
              <span className="flex w-full items-center gap-2">
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate text-fbui-text-secondary',
                    s.previewText
                  )}
                >
                  {item.detail}
                </span>
                <CaretDown
                  aria-hidden="true"
                  className="size-3.5 shrink-0 text-fbui-gray-400"
                />
              </span>
            )}
          </button>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <p className={summaryClass}>{sentence}</p>
            {/* flex (not a bare span) so the wrapper sizes to the time's own line
                box, not the inherited 24px one — otherwise it inflates the row and
                the text stops centering. */}
            <span className="flex shrink-0 items-center pt-0.5">{time}</span>
          </div>
        )}
        {/* Non-expandable detail (chips, "Request cancelled") — always shown. */}
        {!expandable && item.detail != null && (
          <div className={cn('text-fbui-text-body', s.detail)}>
            {item.detail}
          </div>
        )}
        {expandable && open && (
          <div
            id={panelId}
            className={cn(
              'mt-2 border-t border-fbui-gray-100 pt-2 text-fbui-text-body',
              size === 'sm' ? 'text-xs' : 'text-sm'
            )}
          >
            {item.expandedContent}
          </div>
        )}
      </div>
    </li>
  )
}

export const ActivityTimeline = React.forwardRef<
  HTMLDivElement,
  ActivityTimelineProps
>(
  (
    {
      items,
      collapseAfter,
      now,
      emptyState,
      tintedIcons,
      size = 'md',
      className,
      ...props
    },
    ref
  ) => {
  const groups = groupByDay(items, now ?? new Date())
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set())
  const s = timelineSize[size]

  if (items.length === 0 && emptyState != null) {
    return (
      <div ref={ref} className={cn('text-fbui-text-secondary', className)} {...props}>
        {emptyState}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={cn('flex flex-col', s.groupGap, className)}
      {...props}
    >
      {groups.map((group) => {
        const isExpanded = expanded.has(group.key)
        const limit = collapseAfter ?? group.items.length
        const visible = isExpanded ? group.items : group.items.slice(0, limit)
        const hidden = group.items.length - visible.length
        return (
          <section key={group.key}>
            {/* The <h3> labels the section (navigable by heading); no aria-label
                on the section so it isn't a redundant region landmark per day. */}
            <div className="mb-2 flex items-center gap-3">
              <h3
                className={cn(
                  'shrink-0 text-fbui-text-secondary',
                  s.dayLabel
                )}
              >
                {group.label}
              </h3>
              <span className="h-px flex-1 bg-fbui-gray-200" aria-hidden="true" />
            </div>
            <div className="rounded-xl bg-fbui-gray-50 p-2">
              <ol className="flex flex-col">
                {visible.map(({ item, date }, i) => (
                  <ActivityRow
                    key={item.id}
                    item={item}
                    date={date}
                    isFirst={i === 0}
                    isLast={i === visible.length - 1}
                    tinted={tintedIcons}
                    size={size}
                  />
                ))}
              </ol>
              {/* Show the toggle whenever the day has more rows than the
                  collapse limit — "+N more" to expand, "Show less" to collapse
                  back (the same button flips both ways). */}
              {collapseAfter != null && group.items.length > collapseAfter && (
                <div className="flex justify-end px-2 pb-1 pt-2">
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((prev) => {
                        const next = new Set(prev)
                        if (next.has(group.key)) next.delete(group.key)
                        else next.add(group.key)
                        return next
                      })
                    }
                    className="rounded text-sm font-medium text-fbui-basil-700 outline-none transition-colors hover:text-fbui-basil-800 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
                  >
                    {isExpanded ? 'Show less' : `+${hidden} more`}
                  </button>
                </div>
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
})
ActivityTimeline.displayName = 'ActivityTimeline'
